import api from './api';
import { Transfer, TransferFilters, DashboardStats, WeeklyTrend, StatusDistribution, MonthlyComparison, VehicleTypeDistribution } from '../types';

export const transfersService = {
  getAll: async (filters?: TransferFilters): Promise<{ data: Transfer[]; total: number }> => {
    const response = await api.get<Transfer[] | { data: Transfer[]; total: number }>('/transfers', { params: filters });
    const raw = response.data as any;
    if (Array.isArray(raw)) return { data: raw, total: raw.length };
    return raw;
  },

  getById: async (id: string): Promise<Transfer> => {
    const response = await api.get<Transfer>(`/transfers/${id}`);
    return response.data;
  },

  create: async (data: Partial<Transfer>): Promise<Transfer> => {
    const response = await api.post<Transfer>('/transfers', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Transfer>): Promise<Transfer> => {
    const response = await api.patch<Transfer>(`/transfers/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/transfers/${id}`);
  },

  // Marketplace: public pool of available transfers
  getPool: async (filters?: TransferFilters): Promise<Transfer[]> => {
    const params: Record<string, string> = {};
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;
    if (filters?.search) params.search = filters.search;
    if (filters?.tripType) params.tripType = filters.tripType;
    const response = await api.get<Transfer[]>('/transfers/pool', { params });
    return response.data;
  },

  claimTransfer: async (id: string): Promise<Transfer> => {
    const response = await api.post<Transfer>(`/transfers/${id}/claim`);
    return response.data;
  },

  // Analytics
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>('/transfers/stats');
    return response.data;
  },

  getWeeklyTrends: async (): Promise<WeeklyTrend[]> => {
    const response = await api.get<WeeklyTrend[]>('/transfers/analytics/weekly-trends');
    return response.data;
  },

  getStatusDistribution: async (): Promise<StatusDistribution[]> => {
    const response = await api.get<StatusDistribution[]>('/transfers/analytics/status-distribution');
    return response.data;
  },

  getMonthlyComparison: async (): Promise<MonthlyComparison[]> => {
    const response = await api.get<MonthlyComparison[]>('/transfers/analytics/monthly-comparison');
    return response.data;
  },

  getVehicleTypeDistribution: async (): Promise<VehicleTypeDistribution[]> => {
    const response = await api.get<VehicleTypeDistribution[]>('/transfers/analytics/vehicle-type-distribution');
    return response.data;
  },

  // Excel import
  downloadImportTemplate: async (): Promise<void> => {
    const response = await api.get('/transfers/import/template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'transfer-import-template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  previewImportExcel: async (file: File): Promise<{ rows: any[]; validCount: number; errorCount: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/transfers/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  executeImport: async (rows: any[]): Promise<{ created: number; failed: number; errors: { row: number; message: string }[] }> => {
    const response = await api.post('/transfers/import/execute', { rows });
    return response.data;
  },
};
